import React, { useCallback, useEffect, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { getFns } from '../../firebase'
import Button from '../ui/Button'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card'
import Input from '../ui/Input'
import Icon from '../AppIcon'

/**
 * Operators & PINs (Auth v2): admin-only management of the operator list.
 * PINs are hashed server-side by the operatorAdminUpsert callable and are
 * never readable from the client.
 */
export default function OperatorsPanel() {
  const [operators, setOperators] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [newName, setNewName] = useState('')
  const [newPin, setNewPin] = useState('')
  const [saving, setSaving] = useState(false)
  const [pinEdits, setPinEdits] = useState({})
  const [busyId, setBusyId] = useState(null)
  // PINs identify who ran a transaction (POS-style), not security — the shop
  // login is the security layer. Admins see them; masked until toggled.
  const [shownPins, setShownPins] = useState({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const call = httpsCallable(getFns(), 'operatorAdminList')
      const { data } = await call({})
      setOperators(data?.operators || [])
      setError('')
    } catch (err) {
      setError(err?.message || 'Failed to load operators')
      setOperators([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const upsert = useCallback(async (payload) => {
    const call = httpsCallable(getFns(), 'operatorAdminUpsert')
    await call(payload)
    await load()
  }, [load])

  const addOperator = async () => {
    const name = newName.trim()
    const pin = newPin.trim()
    if (!name || !pin) { setError('Name and PIN are required'); return }
    setSaving(true)
    setError('')
    try {
      await upsert({ name, pin, active: true })
      setNewName('')
      setNewPin('')
    } catch (err) {
      setError(err?.message || 'Failed to add operator')
    } finally {
      setSaving(false)
    }
  }

  const setPin = async (id) => {
    const pin = String(pinEdits[id] || '').trim()
    if (!pin) return
    setBusyId(id)
    setError('')
    try {
      await upsert({ id, pin })
      setPinEdits((prev) => ({ ...prev, [id]: '' }))
    } catch (err) {
      setError(err?.message || 'Failed to update PIN')
    } finally {
      setBusyId(null)
    }
  }

  const setActive = async (id, active) => {
    setBusyId(id)
    setError('')
    try {
      await upsert({ id, active })
    } catch (err) {
      setError(err?.message || 'Failed to update operator')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Card variant="solid" hover="none">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Operators & PINs</CardTitle>
            <CardDescription>
              Each person identifies themselves with an individual PIN (4-8 digits) when
              running a transaction — like a POS operator code. Admins can view PINs here.
            </CardDescription>
          </div>
          <Icon name="KeyRound" size={24} className="text-primary" />
        </div>
      </CardHeader>
      <CardContent>
        {/* Add operator */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <Input
              aria-label="Operator name"
              placeholder="Operator name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="h-10"
            />
          </div>
          <Input
            type="password"
            aria-label="PIN (4-8 digits)"
            placeholder="PIN (4-8 digits)"
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputMode="numeric"
            autoComplete="new-password"
            className="h-10 w-40"
          />
          <Button variant="default" size="default" iconName="UserPlus" onClick={addOperator} loading={saving}>
            Add Operator
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-xs text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground p-4">Loading operators…</p>
        ) : (
          <div className="space-y-3">
            {operators.map((op) => (
              <div key={op.id} className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 min-w-[160px] flex-1">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                  <span className="font-medium text-sm">{op.name || '(unnamed)'}</span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${op.active === false ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${op.active === false ? 'bg-destructive' : 'bg-success'}`} />
                    {op.active === false ? 'Disabled' : 'Active'}
                  </span>
                  {!op.hasPin && (
                    <span className="text-xs text-warning font-medium">No PIN set</span>
                  )}
                  {op.pin && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/60 text-xs font-mono font-semibold text-foreground tracking-widest">
                      {shownPins[op.id] ? op.pin : '•'.repeat(String(op.pin).length)}
                      <button
                        type="button"
                        onClick={() => setShownPins((prev) => ({ ...prev, [op.id]: !prev[op.id] }))}
                        aria-label={shownPins[op.id] ? `Hide PIN for ${op.name || 'operator'}` : `Show PIN for ${op.name || 'operator'}`}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Icon name={shownPins[op.id] ? 'EyeOff' : 'Eye'} size={14} />
                      </button>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    aria-label={`New PIN for ${op.name || 'operator'}`}
                    placeholder="New PIN"
                    value={pinEdits[op.id] || ''}
                    onChange={(e) => setPinEdits((prev) => ({ ...prev, [op.id]: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                    inputMode="numeric"
                    autoComplete="new-password"
                    className="h-8 w-28 text-xs"
                  />
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => setPin(op.id)}
                    disabled={busyId === op.id || !(pinEdits[op.id] || '').trim()}
                  >
                    Set PIN
                  </Button>
                  <Button
                    variant={op.active === false ? 'default' : 'outline'}
                    size="xs"
                    iconName={op.active === false ? 'Check' : 'Ban'}
                    onClick={() => setActive(op.id, op.active === false)}
                    disabled={busyId === op.id}
                  >
                    {op.active === false ? 'Enable' : 'Disable'}
                  </Button>
                </div>
              </div>
            ))}
            {operators.length === 0 && (
              <div className="rounded-xl border border-border bg-card p-8 text-center">
                <Icon name="KeyRound" size={48} className="mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No operators yet</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Add each staff member with their own PIN</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
